import { streamingRemoteFeedback, streamingResponseText, streamingFieldText, streamingFeedbackText } from "../i18n/streamingFeedbackPresentation";
import { type StreamingMessage, useStreamingLanguage, streamMessage, streamException, streamLocal, streamFeedback } from "../i18n/streamingDashboardPresentation";
/**
 * useUserProfile / useUserHistory -- React hooks for WSD User Drawer data fetching.
 *
 * Fetches user profile (upstream + local stats) and paginated watch history
 * with time-range filtering and breakdowns.
 *
 * Endpoints:
 * - GET /api/streaming/users/:serverType/:userId
 * - GET /api/streaming/users/:serverType/:userId/history
 */
import { useCallback, useEffect, useRef, useState } from "react";

// ============================================================================
// Types (mirrored from backend userProfileService.ts)
// ============================================================================

export interface UserPolicy {
  enableRemoteAccess: boolean;
  simultaneousStreamLimit: number | null;
  remoteClientBitrateLimit: number | null;
  enableMediaPlayback: boolean;
  enableVideoPlaybackTranscoding: boolean;
  enableAudioPlaybackTranscoding: boolean;
  enableSyncTranscoding: boolean;
  enableContentDeletion: boolean;
  enableContentDownloading: boolean;
  // Expanded policy fields
  isHiddenRemotely: boolean;
  isHiddenFromUnusedDevices: boolean;
  enablePlaybackRemuxing: boolean;
  enableMediaConversion: boolean;
  autoRemoteQuality: number;
  enableAllFolders: boolean;
  enabledFolders: string[];
  enabledFolderNames: string[];
  excludedSubFolders: string[];
  excludedSubFolderNames: string[];
  enableAllChannels: boolean;
  enableAllDevices: boolean;
  enableSubtitleDownloading: boolean;
  enableSubtitleManagement: boolean;
  maxParentalRating: number;
  blockedTags: string[];
  blockUnratedItems: string[];
  includeTags: string[];
  isTagBlockingModeInclusive: boolean;
  allowTagOrRating: boolean;
  enablePublicSharing: boolean;
  allowSharingPersonalItems: boolean;
  allowCameraUpload: boolean;
  enableLiveTvAccess: boolean;
  enableLiveTvManagement: boolean;
  enableRemoteControlOfOtherUsers: boolean;
  enableSharedDeviceControl: boolean;
  enableUserPreferenceAccess: boolean;
  restrictedFeatures: string[];
}

export interface UserConfig {
  audioLanguagePreference: string;
  subtitleLanguagePreference: string;
  subtitleMode: string;
  playDefaultAudioTrack: boolean;
  displayMissingEpisodes: boolean;
  enableNextEpisodeAutoPlay: boolean;
  hidePlayedInLatest: boolean;
  rememberAudioSelections: boolean;
  rememberSubtitleSelections: boolean;
  introSkipMode: string;
}

export interface UserStats {
  totalWatchTimeSecs: number;
  sessionsCount: number;
  avgCompletionPct: number;
  firstSeenAt: number | null;
  lastSeenAt: number | null;
  uniqueDevices: number;
  uniqueTitles: number;
}

export interface UserFavorites {
  topLibraries: Array<{ name: string; count: number }>;
  topGenres: Array<{ name: string; count: number }>;
}

export interface UserProfile {
  userId: string;
  userName: string;
  serverType: "emby" | "jellyfin";
  isAdmin: boolean;
  isDisabled: boolean;
  isHidden: boolean;
  hasPassword: boolean;
  lastLoginDate: string | null;
  lastActivityDate: string | null;
  hasAvatar: boolean;
  avatarUrl: string | null;
  policy: UserPolicy;
  config: UserConfig;
  stats: UserStats;
  activeSessions: number;
  favorites: UserFavorites | null;
  availableLibraries: Array<{
    id: string;
    name: string;
    subFolders: Array<{ compositeId: string; path: string }>;
  }>;
}

export interface HistoryPlayEntry {
  id: number;
  mediaTitle: string;
  mediaType: string;
  seriesName: string | null;
  seasonNumber: number | null;
  episodeNumber: number | null;
  nowPlaying: string | null;
  watchDurationSecs: number;
  completionPct: number;
  playMethod: string | null;
  deviceName: string;
  ipAddress: string | null;
  geoCity: string | null;
  geoCountryCode: string | null;
  geoLatitude: number | null;
  geoLongitude: number | null;
  startedAt: number;
  stoppedAt: number | null;
  sourceLabel: string | null;
  sourceColor: string | null;
  mediaItemId: string | null;
  serverType: string | null;
}

export interface HistorySummary {
  totalWatchTimeSecs: number;
  sessionsCount: number;
  avgCompletionPct: number;
}

export interface DeviceBreakdownEntry {
  deviceId: string | null;
  deviceName: string;
  sessionsCount: number;
  watchTimeSecs: number;
}

export interface MediaTypeBreakdownEntry {
  mediaType: string;
  sessionsCount: number;
  watchTimeSecs: number;
}

export interface PlayMethodBreakdownEntry {
  playMethod: string;
  sessionsCount: number;
  watchTimeSecs: number;
}

export interface UserWatchHistory {
  total: number;
  limit: number;
  offset: number;
  window: HistoryWindow;
  items: HistoryPlayEntry[];
  summary: HistorySummary;
  byDevice: DeviceBreakdownEntry[];
  byMediaType: MediaTypeBreakdownEntry[];
  byPlayMethod: PlayMethodBreakdownEntry[];
}

export type HistoryWindow = "7d" | "30d" | "180d" | "all";

// ============================================================================
// Cache
// ============================================================================

interface CachedProfile {
  profile: UserProfile;
  fetchedAt: number;
}

interface CachedHistory {
  history: UserWatchHistory;
  fetchedAt: number;
}

const profileCache = new Map<string, CachedProfile>();
const historyCache = new Map<string, CachedHistory>();
const CACHE_TTL_MS = 3 * 60 * 1000; // 3 minutes

function buildProfileKey(serverType: string, userId: string): string {
  return `${serverType}:${userId}`;
}

function buildHistoryKey(serverType: string, userId: string, window: HistoryWindow): string {
  return `${serverType}:${userId}:${window}`;
}

// ============================================================================
// useUserProfile
// ============================================================================

export function useUserProfile(
  serverType: string,
  userId: string,
  enabled: boolean
): {
  profile: UserProfile | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorFeedback, setError] = useState<string | StreamingMessage | null>(null);
  useStreamingLanguage();
  const error = streamMessage(errorFeedback);
  const abortRef = useRef<AbortController | null>(null);
  const fetchedKeyRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  const fetchProfile = useCallback(
    async (force?: boolean) => {
      if (!serverType || !userId || !enabled) return;

      const key = buildProfileKey(serverType, userId);

      // Check cache (unless forced)
      if (!force) {
        const cached = profileCache.get(key);
        if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
          setProfile(cached.profile);
          setError(null);
          fetchedKeyRef.current = key;
          return;
        }
      }

      // Skip if already fetching this key (use ref to avoid re-render dependency)
      if (isFetchingRef.current && fetchedKeyRef.current === key) return;

      // Abort any in-flight request
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);

      try {
        const url = `/api/streaming/users/${encodeURIComponent(serverType)}/${encodeURIComponent(userId)}`;
        const res = await fetch(url, { signal: controller.signal });
        const data = await res.json();

        if (!res.ok || !data.ok || !data.user) {
          throw streamException((streamingRemoteFeedback(data) ?? data.message ?? streamLocal("failedToLoadUserProfile")));
        }

        const fetchedProfile = data.user as UserProfile;
        setProfile(fetchedProfile);
        setError(null);

        // Cache
        profileCache.set(key, {
          profile: fetchedProfile,
          fetchedAt: Date.now(),
        });
        fetchedKeyRef.current = key;
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        const message = streamFeedback(err, "failedToLoadUserProfile");
        setError(message);
      } finally {
        isFetchingRef.current = false;
        setIsLoading(false);
      }
    },
    [serverType, userId, enabled]
  );

  // Fetch on mount or when key changes
  useEffect(() => {
    if (!enabled) {
      setProfile(null);
      setError(null);
      fetchedKeyRef.current = null;
      return;
    }

    const key = buildProfileKey(serverType, userId);
    if (fetchedKeyRef.current !== key) {
      void fetchProfile();
    }

    return () => {
      abortRef.current?.abort();
    };
  }, [serverType, userId, enabled, fetchProfile]);

  const refetch = useCallback(() => {
    void fetchProfile(true);
  }, [fetchProfile]);

  return { profile, isLoading, error, refetch };
}

// ============================================================================
// useUserHistory
// ============================================================================

export function useUserHistory(
  serverType: string,
  userId: string,
  window: HistoryWindow,
  enabled: boolean
): {
  history: UserWatchHistory | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const [history, setHistory] = useState<UserWatchHistory | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorFeedback, setError] = useState<string | StreamingMessage | null>(null);
  useStreamingLanguage();
  const error = streamMessage(errorFeedback);
  const abortRef = useRef<AbortController | null>(null);
  const fetchedKeyRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  const fetchHistory = useCallback(
    async (force?: boolean) => {
      if (!serverType || !userId || !enabled) return;

      const key = buildHistoryKey(serverType, userId, window);

      // Check cache (unless forced)
      if (!force) {
        const cached = historyCache.get(key);
        if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
          setHistory(cached.history);
          setError(null);
          fetchedKeyRef.current = key;
          return;
        }
      }

      // Skip if already fetching this key (use ref to avoid re-render dependency)
      if (isFetchingRef.current && fetchedKeyRef.current === key) return;

      // Abort any in-flight request
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);

      try {
        const url = `/api/streaming/users/${encodeURIComponent(serverType)}/${encodeURIComponent(userId)}/history?window=${encodeURIComponent(window)}&limit=20&offset=0`;
        const res = await fetch(url, { signal: controller.signal });
        const data = await res.json();

        if (!res.ok || !data.ok || !data.history) {
          throw streamException((streamingRemoteFeedback(data) ?? data.message ?? streamLocal("failedToLoadWatchHistory")));
        }

        const fetchedHistory = data.history as UserWatchHistory;
        setHistory(fetchedHistory);
        setError(null);

        // Cache
        historyCache.set(key, {
          history: fetchedHistory,
          fetchedAt: Date.now(),
        });
        fetchedKeyRef.current = key;
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        const message = streamFeedback(err, "failedToLoadWatchHistory");
        setError(message);
      } finally {
        isFetchingRef.current = false;
        setIsLoading(false);
      }
    },
    [serverType, userId, window, enabled]
  );

  // Fetch on mount or when key changes
  useEffect(() => {
    if (!enabled) {
      setHistory(null);
      setError(null);
      fetchedKeyRef.current = null;
      return;
    }

    const key = buildHistoryKey(serverType, userId, window);
    if (fetchedKeyRef.current !== key) {
      void fetchHistory();
    }

    return () => {
      abortRef.current?.abort();
    };
  }, [serverType, userId, window, enabled, fetchHistory]);

  const refetch = useCallback(() => {
    void fetchHistory(true);
  }, [fetchHistory]);

  return { history, isLoading, error, refetch };
}
